import React from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import { black, feedbackGreenLight, feedbackOrangeLight, gray5, primary } from 'styles/global_defaults/colors';
import { normalFontWeight, openSans, semiBoldFontWeight, textMediumFontSize, textMediumLineHeight, textSmallLineHeight } from 'styles/global_defaults/fonts';
import { halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { moss, sandstone } from 'athena/styles/colors';

import NvIcon from 'shared/components/nv-icon';

export type SectionKind = 'did-well' | 'can-grow' | 'worked' | 'improved';

type Props = {
  kind: SectionKind;
  content?: string;
  onRevise?: () => void;
};

type SectionConfig = {
  iconName: string;
  iconColor: string;
  iconBgColor: string;
  labelKey: () => string;
  dataQa: string;
};

const getConfig = (kind: SectionKind): SectionConfig => {
  switch (kind) {
    case 'did-well':
      return {
        iconName: 'like',
        iconColor: moss,
        iconBgColor: feedbackGreenLight,
        labelKey: t.SUBMISSIONS.AI_ASSIGNMENT.DID_WELL,
        dataQa: config.pendo.submissionView.aiInsights.sectionDidWell,
      };
    case 'can-grow':
      return {
        iconName: 'ideas',
        iconColor: sandstone,
        iconBgColor: feedbackOrangeLight,
        labelKey: t.SUBMISSIONS.AI_ASSIGNMENT.CAN_GROW,
        dataQa: config.pendo.submissionView.aiInsights.sectionCanGrow,
      };
    case 'worked':
      return {
        iconName: 'like',
        iconColor: moss,
        iconBgColor: feedbackGreenLight,
        labelKey: t.SUBMISSIONS.AI_ASSIGNMENT.WORKED,
        dataQa: config.pendo.submissionView.aiInsights.sectionWorked,
      };
    case 'improved':
    default:
      return {
        iconName: 'ideas',
        iconColor: sandstone,
        iconBgColor: feedbackOrangeLight,
        labelKey: t.SUBMISSIONS.AI_ASSIGNMENT.IMPROVED,
        dataQa: config.pendo.submissionView.aiInsights.sectionImproved,
      };
  }
};

const styles = css`
  display: flex;
  flex-direction: column;

  .section-header {
    display: flex;
    align-items: center;
    gap: ${halfSpacing}px;
    padding: ${halfSpacing}px 0;
    border-bottom: 1px solid ${gray5};
  }

  .section-icon {
    width: 36px;
    height: 36px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .section-label {
    margin: 0;
    font-family: ${openSans};
    font-weight: ${semiBoldFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${textSmallLineHeight}px;
    color: ${black};
  }

  .section-body {
    padding: ${halfSpacing}px 0 0 ${standardSpacing + 4}px;
    font-family: ${openSans};
    font-weight: ${normalFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${textMediumLineHeight}px;
    color: ${black};
  }

  .revise-cta {
    margin-top: ${halfSpacing}px;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: ${primary};
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Section: React.FC<Props> = ({ kind, content, onRevise }) => {
  if (!content) {
    return null;
  }

  const { iconName, iconColor, iconBgColor, labelKey, dataQa } = getConfig(kind);
  const reviseDataQa = config.pendo.submissionView.aiInsights.reviseSubmission;

  const iconWrapperCss = css`
    background-color: ${iconBgColor};
    color: ${iconColor};
  `;

  return (
    <div css={styles} data-qa={dataQa}>
      <div className='section-header'>
        <div className='section-icon' css={iconWrapperCss}>
          <NvIcon icon={iconName} size='smallest' aria-hidden />
        </div>
        <h3 className='section-label'>{labelKey()}</h3>
      </div>
      <div className='section-body'>
        {/* eslint-disable-next-line react/no-danger */}
        <span dangerouslySetInnerHTML={{ __html: content }} />
        {onRevise && (
          <div>
            <button
              type='button'
              className='revise-cta'
              onClick={onRevise}
              data-qa={reviseDataQa}
            >
              {t.SUBMISSIONS.AI_ASSIGNMENT.REVISE_SUBMISSION()}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Section;
